import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { getDb } from '@talk2nick/shared';
import {
  createDefaultPrivacySettings,
  resolvePrivacySettings,
  type PrivacySettings,
} from '@talk2nick/core';

/**
 * `privacy/{uid}` 읽기/쓰기 (PLAN 5-10장 경량 프라이빗 모드).
 *
 * 문서 하나가 이 기기 사용자의 공개 범위 전체를 담는다: 알림·읽음표시·타이핑·
 * 최근 접속 노출·인앱 배너·잠금 화면 미리보기·모바일 데이터 자동다운로드.
 *
 * Rules가 `isSelf(uid)`만 허용하므로 남의 설정을 볼 수 없다. 그래서 **기본값 해석은
 * core의 `resolvePrivacySettings` 하나로 통일**한다 — 클라이언트와 Functions의
 * 해석이 갈리면 "푸시에는 본문이 뜬다" 같은 어긋남이 생긴다.
 */

export interface PrivacyState {
  settings: PrivacySettings;
  loading: boolean;
}

/** privacy 문서 구독 — 설정 화면·NotificationHost·MediaContent가 함께 쓴다 */
export function usePrivacy(uid: string | null, enabled = true): PrivacyState {
  const [state, setState] = useState<PrivacyState>({
    settings: createDefaultPrivacySettings(),
    loading: true,
  });

  useEffect(() => {
    if (!uid || !enabled) {
      setState({ settings: createDefaultPrivacySettings(), loading: false });
      return;
    }
    const unsubscribe = onSnapshot(
      doc(getDb(), 'privacy', uid),
      (snap) => {
        setState({
          settings: resolvePrivacySettings(
            snap.exists() ? (snap.data() as Partial<PrivacySettings>) : undefined,
          ),
          loading: false,
        });
      },
      (error: unknown) => {
        console.error('[talk2nIcK] 개인정보 설정 구독 실패:', error);
        setState({ settings: createDefaultPrivacySettings(), loading: false });
      },
    );
    return unsubscribe;
  }, [uid, enabled]);

  return state;
}

/**
 * privacy 문서 부분 갱신.
 *
 * **patch가 아니라 전체 저장**이다 — listen 규칙이 그대로 통과하려면 현재 구독값을
 * 그대로 다시 써야 하고, 두 기기에서 동시에 켜도 마지막 쓰기가 이긴다(경합 시 안전).
 */
export async function setPrivacy(
  uid: string,
  current: PrivacySettings,
  patch: Partial<PrivacySettings>,
): Promise<void> {
  await setDoc(doc(getDb(), 'privacy', uid), { ...current, ...patch });
}
